'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import type useEmblaCarousel from 'embla-carousel-react';
import { createContext, useContext } from 'react';
import type { CarouselPresetConfig } from './carousel-presets';

// ---------------------------------------------------------------------------
// Context — split into static (preset/orientation) and dynamic (scroll state)
// to prevent CarouselItem from re-rendering on every scroll event.
// ---------------------------------------------------------------------------

export type CarouselStaticContextType = {
  emblaRef: ReturnType<typeof useEmblaCarousel>[0];
  orientation: 'horizontal' | 'vertical';
  presetConfig: CarouselPresetConfig | undefined;
};

export type CarouselDynamicContextType = {
  emblaApi: EmblaCarouselType | undefined;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
  scrollSnaps: number[];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
};

export const CarouselStaticContext = createContext<CarouselStaticContextType | null>(null);
export const CarouselDynamicContext = createContext<CarouselDynamicContextType | null>(null);

export function useCarouselStatic() {
  const context = useContext(CarouselStaticContext);
  if (!context) throw new Error('useCarouselStatic must be used within <Carousel>');
  return context;
}

export function useCarouselDynamic() {
  const context = useContext(CarouselDynamicContext);
  if (!context) throw new Error('useCarouselDynamic must be used within <Carousel>');
  return context;
}
